// Kanjoy Users (Access) page — task 104d.1 D1.3 / D1.4 / D1.5.
//
// Shows the org's Memberships in three sections: Active, Pending invites,
// Former members. Admins can invite new Kanjoy users, change roles, revoke
// access, and reactivate former members. In mockup mode the mutations are
// no-ops — the fixtures are read-only, mirroring the 104c precedent.
//
// This page is the "Access" surface. The separate "Employees" surface at
// `/settings/team` ships in 104c; the two are intentionally distinct
// (Employees = billable roster, Access = Kanjoy user roster).
//
// Live-mode wiring (real API + mutation hooks) lands in 104d.2; 104d.1 is
// a mockup-mode-only prototype for design review.

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DataTable } from '@/components/composites/DataTable';
import { InviteMemberModal } from '@/components/composites/InviteMemberModal';
import { VisibilityEditorModal } from '@/components/composites/VisibilityEditorModal';
import { Button } from '@/components/primitives/Button';
import { Badge } from '@/components/primitives/Badge';
import { ActionLink } from '@/components/primitives/ActionLink';
import { TextInput } from '@/components/primitives/TextInput';
import { Toggle } from '@/components/primitives/Toggle';
import { useAppContext } from '@/context/AppContext';
import { formatDayFull } from '@/lib/dates';
import {
  useOrgAccess,
  useResendInvite,
  useRevokeInvite,
  useChangeRole,
  useRevokeMembership,
  useReactivateMembership,
  type KanjoyUser,
  type KanjoyUserRole,
  type PendingInvite,
} from '@/hooks/useOrgAccess';
import { useEmployees } from '@/hooks/useEmployees';
import type { BadgeVariant } from '@/types/badge';
import type { StatCardModel } from '@/types/stats';
import { FROZEN_NOW } from '@/lib/frozenTime';
import styles from './SettingsAccessPage.module.css';

// ── Role helpers ─────────────────────────────────────────────────────────

const ROLE_LABEL: Record<KanjoyUserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

const ROLE_BADGE_VARIANT: Record<KanjoyUserRole, BadgeVariant> = {
  owner: 'owner',
  admin: 'admin',
  member: 'member',
};

function RoleBadge({ role }: { role: KanjoyUserRole }) {
  return <Badge variant={ROLE_BADGE_VARIANT[role]}>{ROLE_LABEL[role]}</Badge>;
}

// 104f D5: human-readable copy for the five-value visibility enum. Kept
// here (not shared) so this page owns the exact labels the design
// reference in task 104f § D5 specifies.
function describeVisibilityScope(
  scope: 'self' | 'all' | 'own_bu' | 'bu_list' | 'employee_list',
): string {
  switch (scope) {
    case 'self':
      return 'Self only';
    case 'all':
      return 'All activity';
    case 'own_bu':
      return 'Own BU';
    case 'bu_list':
      return 'BU list';
    case 'employee_list':
      return 'Employee list';
  }
}

// ── Column keys ──────────────────────────────────────────────────────────

type ActiveCol = 'name' | 'email' | 'role' | 'linked' | 'visibility' | 'joined' | 'actions';
type PendingCol = 'email' | 'role' | 'invitedBy' | 'expires' | 'status' | 'actions';
type FormerCol = 'name' | 'email' | 'deactivatedAt' | 'actions';

// ── Page ─────────────────────────────────────────────────────────────────

export function SettingsAccessPage() {
  const { currentUser } = useAppContext();
  const isAdmin = currentUser.role === 'admin';
  const [search, setSearch] = useState('');
  const [showFormer, setShowFormer] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // 104f D5 — visibility editor state: null when closed; the target row
  // when open.
  const [visibilityTarget, setVisibilityTarget] = useState<KanjoyUser | null>(null);

  const { users, pendingInvites } = useOrgAccess();
  const { data: employees } = useEmployees();
  // Mutation hooks (104d.2). In mockup mode these short-circuit to resolved
  // promises so the existing fixture-backed flows keep working without a
  // backend; the fixtures are read-only, so the UI does not update — this
  // mirrors the 104c precedent on the Employees page.
  const changeRole = useChangeRole();
  const revokeMembership = useRevokeMembership();
  const reactivateMembership = useReactivateMembership();
  const resendInvite = useResendInvite();
  const revokeInvite = useRevokeInvite();

  const employeeById = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const e of employees) map.set(e.id, { id: e.id, name: e.name });
    return map;
  }, [employees]);

  const needle = search.trim().toLowerCase();

  const activeUsers = useMemo(() => {
    return users
      .filter((u) => u.deactivatedAt === null)
      .filter((u) => {
        if (!needle) return true;
        return `${u.name} ${u.email}`.toLowerCase().includes(needle);
      });
  }, [users, needle]);

  const formerUsers = useMemo(() => {
    return users
      .filter((u) => u.deactivatedAt !== null)
      .filter((u) => {
        if (!needle) return true;
        return `${u.name} ${u.email}`.toLowerCase().includes(needle);
      });
  }, [users, needle]);

  const ownerCount = activeUsers.filter((u) => u.role === 'owner').length;
  const adminCount = activeUsers.filter((u) => u.role === 'admin').length;
  const memberCount = activeUsers.filter((u) => u.role === 'member').length;

  const stats: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Owners', value: String(ownerCount) },
    { label: 'Admins', value: String(adminCount) },
    { label: 'Members', value: String(memberCount) },
    { label: 'Pending invites', value: String(pendingInvites.length) },
  ];

  // ── Active members columns ─────────────────────────────────────────────

  const activeColumns: Array<{
    key: ActiveCol;
    header: string;
    align?: 'left' | 'right';
    render: (row: KanjoyUser) => ReactNode;
  }> = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className={styles.name}>{row.name}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) => <span className={styles.email}>{row.email}</span>,
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => <RoleBadge role={row.role} />,
    },
    {
      key: 'linked',
      header: 'Linked Employee',
      render: (row) => {
        if (!row.employeeId) return <span className={styles.mutedRow}>{'—'}</span>;
        const emp = employeeById.get(row.employeeId);
        if (!emp) return <span className={styles.mutedRow}>{'—'}</span>;
        return (
          <Link to="/settings/team" className={styles.linkedEmployee}>
            {emp.name}
          </Link>
        );
      },
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (row) => {
        // 104f D5: owner / admin always see all — note the override
        // alongside the stored scope so the admin understands why the
        // editor's scope value is "cosmetic".
        const label = describeVisibilityScope(row.visibilityScope);
        if (row.role === 'owner' || row.role === 'admin') {
          return (
            <span className={styles.muted}>
              {label}{' '}
              <span className={styles.overrideNote}>(Overridden — {row.role} sees all)</span>
            </span>
          );
        }
        return <span>{label}</span>;
      },
    },
    {
      key: 'joined',
      header: 'Joined',
      render: (row) => <span className={styles.muted}>{formatDayFull(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        if (!isAdmin) return null;
        // Last-owner guardrail: if this is the only active owner, both
        // role-change and revoke are disabled with an explanatory tooltip.
        const isLastOwner = row.role === 'owner' && ownerCount <= 1;
        const lastOwnerTitle = 'At least one owner must remain';
        return (
          <span className={styles.actions}>
            <ActionLink
              disabled={isLastOwner}
              title={isLastOwner ? lastOwnerTitle : undefined}
              onClick={() => {
                // Minimal role picker — clicking rotates admin ↔ member
                // for now (owner transitions require the dedicated picker
                // UI that 104f extends). This keeps the hook wired without
                // shipping a new modal in 104d.2.
                if (row.role === 'owner') return;
                const nextRole: KanjoyUserRole = row.role === 'admin' ? 'member' : 'admin';
                changeRole.mutate({ membershipId: row.id, role: nextRole });
              }}
            >
              Edit role
            </ActionLink>
            <ActionLink
              onClick={() => setVisibilityTarget(row)}
              data-testid={`edit-visibility-${row.id}`}
            >
              Edit visibility
            </ActionLink>
            <ActionLink
              danger
              disabled={isLastOwner}
              title={isLastOwner ? lastOwnerTitle : undefined}
              onClick={() => {
                revokeMembership.mutate(row.id);
              }}
            >
              Revoke access
            </ActionLink>
          </span>
        );
      },
    },
  ];

  // ── Pending invites columns ────────────────────────────────────────────

  const pendingColumns: Array<{
    key: PendingCol;
    header: string;
    align?: 'left' | 'right';
    render: (row: PendingInvite) => ReactNode;
  }> = [
    {
      key: 'email',
      header: 'Email',
      render: (row) => <span className={styles.email}>{row.email}</span>,
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => <RoleBadge role={row.role} />,
    },
    {
      key: 'invitedBy',
      header: 'Invited by',
      render: (row) => row.createdBy,
    },
    {
      key: 'expires',
      header: 'Expires',
      render: (row) => <span className={styles.muted}>{formatDayFull(row.expiresAt)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        // ISO-8601 timestamps sort lexicographically — cheaper than Date.
        const expired = row.expiresAt < FROZEN_NOW;
        return expired ? (
          <Badge variant="disabled">Expired</Badge>
        ) : (
          <Badge variant="general">Pending</Badge>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        if (!isAdmin) return null;
        // ISO-8601 timestamps sort lexicographically — cheaper than Date.
        const expired = row.expiresAt < FROZEN_NOW;
        return (
          <span className={styles.actions}>
            {!expired && (
              <ActionLink
                onClick={() => {
                  resendInvite.mutate(row.id);
                }}
              >
                Resend
              </ActionLink>
            )}
            <ActionLink
              danger
              onClick={() => {
                revokeInvite.mutate(row.id);
              }}
            >
              Revoke
            </ActionLink>
          </span>
        );
      },
    },
  ];

  // ── Former members columns ─────────────────────────────────────────────

  const formerColumns: Array<{
    key: FormerCol;
    header: string;
    align?: 'left' | 'right';
    render: (row: KanjoyUser) => ReactNode;
  }> = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className={styles.mutedRow}>{row.name}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) => <span className={styles.email}>{row.email}</span>,
    },
    {
      key: 'deactivatedAt',
      header: 'Deactivated',
      render: (row) => (
        <span className={styles.muted}>
          {row.deactivatedAt ? formatDayFull(row.deactivatedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        if (!isAdmin) return null;
        return (
          <span className={styles.actions}>
            <ActionLink
              onClick={() => {
                reactivateMembership.mutate(row.id);
              }}
            >
              Reactivate
            </ActionLink>
          </span>
        );
      },
    },
  ];

  const visibleActiveColumns = isAdmin
    ? activeColumns
    : // 104f D5: Members never see the Visibility column.
      activeColumns.filter((c) => c.key !== 'actions' && c.key !== 'visibility');
  const visiblePendingColumns = isAdmin
    ? pendingColumns
    : pendingColumns.filter((c) => c.key !== 'actions');
  const visibleFormerColumns = isAdmin
    ? formerColumns
    : formerColumns.filter((c) => c.key !== 'actions');

  return (
    <>
      <PageHeader
        title="Access"
        meta={
          <span className={styles.summaryText}>
            {activeUsers.length} users {'·'} {pendingInvites.length} pending invites
          </span>
        }
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setInviteOpen(true)}>
              Invite Member
            </Button>
          ) : undefined
        }
      />
      <div className={styles.statsRow}>
        <StatCardGrid cards={stats} />
      </div>
      <div className={styles.filtersRow} role="group" aria-label="Access filters">
        <div className={styles.searchSlot}>
          <TextInput
            label="Search"
            value={search}
            placeholder="Name or email"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.toggleSlot}>
          <Toggle label="Show former members" checked={showFormer} onChange={setShowFormer} />
        </div>
      </div>

      {/* Section 1: Active members */}
      <section className={styles.section} aria-labelledby="access-active-heading">
        <h2 id="access-active-heading" className={styles.sectionHeading}>
          Active members
        </h2>
        <DataTable<KanjoyUser, ActiveCol>
          caption="Active Kanjoy users"
          columns={visibleActiveColumns}
          rows={activeUsers as KanjoyUser[]}
          getRowKey={(row) => row.id}
          emptyState="No active members match the current filters."
        />
      </section>

      {/* Section 2: Pending invites (only when non-empty) */}
      {pendingInvites.length > 0 && (
        <section className={styles.section} aria-labelledby="access-pending-heading">
          <h2 id="access-pending-heading" className={styles.sectionHeading}>
            Pending invites
          </h2>
          <DataTable<PendingInvite, PendingCol>
            caption="Pending invites"
            columns={visiblePendingColumns}
            rows={pendingInvites as PendingInvite[]}
            getRowKey={(row) => row.id}
          />
        </section>
      )}

      {/* Section 3: Former members (collapsed behind toggle) */}
      {showFormer && (
        <section className={styles.section} aria-labelledby="access-former-heading">
          <h2 id="access-former-heading" className={styles.sectionHeading}>
            Former members
          </h2>
          <DataTable<KanjoyUser, FormerCol>
            caption="Former Kanjoy users"
            columns={visibleFormerColumns}
            rows={formerUsers as KanjoyUser[]}
            getRowKey={(row) => row.id}
            emptyState="No former members."
          />
        </section>
      )}

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        canInviteOwner={currentUser.role === 'admin'}
      />
      <VisibilityEditorModal
        open={visibilityTarget !== null}
        onClose={() => setVisibilityTarget(null)}
        user={visibilityTarget}
      />
    </>
  );
}

// InviteMemberModal extracted to `@/components/composites/InviteMemberModal`
// in 104e D1 so the Employees page can reuse it with a pre-filled locked
// email + pre-committed employeeId.
